import { Suspense, lazy, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Boxes, Copy, ExternalLink, LockKeyhole } from "lucide-react";
import { toast } from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import {
  getAssetStorefrontLabel,
  getAssetStorefrontToneClass,
  normalizeAssetStorefrontMode,
  type AssetStorefrontMode,
} from "@/lib/storefront";
import {
  buildClothingShopHash,
  compactAssetPath,
  formatRenderCollectionLabel,
  formatRenderPrice,
  parseNonNegativeInt,
  resolveRenderAssetDefaultTextureIndex,
} from "@/lib/render-catalog";
import { useAuthStore } from "@/stores/auth";

const RenderAssetViewer = lazy(
  () => import("@/components/render/RenderAssetViewer"),
);

async function copyTextToClipboard(value: string): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard API is unavailable");
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  const successful = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!successful) {
    throw new Error("Không thể copy vào clipboard");
  }
}

export default function RenderCatalogDetailPage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentUser = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    collection,
    gender,
    kind,
    slotId,
    drawableLocalIndex,
    meshPartIndex,
    assetId,
  } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draftPrice, setDraftPrice] = useState("");
  const [priceReason, setPriceReason] = useState("Asset Library update");
  const [priceScope, setPriceScope] = useState<"texture" | "all">("texture");
  const [draftPriceType, setDraftPriceType] =
    useState<AssetStorefrontMode>("money");
  const selectedTextureIndex = parseNonNegativeInt(
    searchParams.get("textureIndex"),
  );
  const selectedAssetID = (assetId ?? "").trim();
  const routeSlotID = parseNonNegativeInt(slotId ?? null);
  const routeDrawableLocalIndex = parseNonNegativeInt(
    drawableLocalIndex ?? null,
  );
  const routeMeshPartIndex = parseNonNegativeInt(meshPartIndex ?? null);
  const canResolveByIdentity =
    Boolean(collection && gender && kind) &&
    routeSlotID !== undefined &&
    routeDrawableLocalIndex !== undefined &&
    routeMeshPartIndex !== undefined;

  const detailQuery = useQuery({
    queryKey: [
      "asset-library-clothes-item",
      collection ?? "",
      gender ?? "",
      kind ?? "",
      routeSlotID ?? -1,
      routeDrawableLocalIndex ?? -1,
      routeMeshPartIndex ?? -1,
    ],
    queryFn: () =>
      api.getAssetLibraryClothesItemByIdentity({
        collection: collection ?? "",
        gender: gender ?? "",
        kind: kind ?? "",
        slotId: routeSlotID ?? 0,
        drawableLocalIndex: routeDrawableLocalIndex ?? 0,
        meshPartIndex: routeMeshPartIndex ?? 0,
      }),
    enabled: canResolveByIdentity,
  });

  useEffect(() => {
    if (
      !detailQuery.data ||
      !collection ||
      !gender ||
      !kind ||
      routeSlotID === undefined ||
      routeDrawableLocalIndex === undefined ||
      routeMeshPartIndex === undefined
    ) {
      return;
    }
    if (detailQuery.data.id === selectedAssetID) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    navigate(
      {
        pathname: `/assets/clothes/${encodeURIComponent(collection)}/${encodeURIComponent(gender)}/${encodeURIComponent(kind)}/${routeSlotID}/${routeDrawableLocalIndex}/${routeMeshPartIndex}/${encodeURIComponent(detailQuery.data.id)}`,
        search: next.toString() ? `?${next.toString()}` : "",
      },
      { replace: true },
    );
  }, [
    collection,
    detailQuery.data,
    gender,
    kind,
    navigate,
    routeDrawableLocalIndex,
    routeMeshPartIndex,
    routeSlotID,
    searchParams,
    selectedAssetID,
  ]);

  useEffect(() => {
    if (!detailQuery.data) {
      return;
    }

    const nextTextureIndex =
      selectedTextureIndex !== undefined &&
      detailQuery.data.textureVariants.some(
        (item) => item.textureIndex === selectedTextureIndex,
      )
        ? selectedTextureIndex
        : resolveRenderAssetDefaultTextureIndex(detailQuery.data);

    if (selectedTextureIndex === nextTextureIndex) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("textureIndex", String(nextTextureIndex));
    setSearchParams(next, { replace: true });
  }, [detailQuery.data, searchParams, selectedTextureIndex, setSearchParams]);

  const detail = detailQuery.data ?? null;
  const effectiveTextureIndex =
    detail &&
    selectedTextureIndex !== undefined &&
    detail.textureVariants.some(
      (item) => item.textureIndex === selectedTextureIndex,
    )
      ? selectedTextureIndex
      : detail
        ? resolveRenderAssetDefaultTextureIndex(detail)
        : 0;
  const selectedTextureVariant =
    detail?.textureVariants.find(
      (item) => item.textureIndex === effectiveTextureIndex,
    ) ??
    detail?.textureVariants[0] ??
    null;
  const resolvedAssetID = detail?.id ?? "";
  const texturePreviewQuery = useQuery({
    queryKey: [
      "asset-library-clothes-texture-preview",
      resolvedAssetID,
      effectiveTextureIndex,
    ],
    queryFn: () =>
      api.getAssetLibraryClothesTexturePreview(
        resolvedAssetID,
        effectiveTextureIndex,
      ),
    enabled: Boolean(detail && resolvedAssetID),
    retry: false,
  });
  const linkedTextureCount =
    detail?.textureVariants.filter((item) => item.pricing).length ?? 0;
  const missingTextureCount = detail
    ? Math.max(detail.textureVariants.length - linkedTextureCount, 0)
    : 0;
  const canSavePrice = Boolean(selectedTextureVariant);
  const canManagePricing = currentUser?.role === "admin";
  const selectedTextureHash = detail
    ? buildClothingShopHash(detail, effectiveTextureIndex)
    : "";

  useEffect(() => {
    setDraftPrice(
      selectedTextureVariant?.pricing
        ? String(selectedTextureVariant.pricing.price)
        : "",
    );
  }, [
    selectedTextureVariant?.pricing?.assetId,
    selectedTextureVariant?.pricing?.price,
  ]);

  useEffect(() => {
    setDraftPriceType(
      normalizeAssetStorefrontMode(selectedTextureVariant?.pricing?.priceType),
    );
  }, [
    selectedTextureVariant?.pricing?.assetId,
    selectedTextureVariant?.pricing?.priceType,
  ]);

  const updatePriceMutation = useMutation({
    mutationFn: (payload: {
      assetId: string;
      textureIndex: number;
      price: number;
      priceType: AssetStorefrontMode;
      scope: "texture" | "all";
    }) =>
      api.updateAssetLibraryClothesTexturePrice(
        payload.assetId,
        payload.textureIndex,
        payload.price,
        payload.priceType,
        priceReason,
        payload.scope,
      ),
    onSuccess: (result) => {
      toast.success(
        result.scope === "all"
          ? `Đã cập nhật storefront và giá cho ${result.updated} texture`
          : "Đã cập nhật storefront và giá cho texture hiện tại",
      );
      queryClient.invalidateQueries({
        queryKey: [
          "asset-library-clothes-item",
          collection ?? "",
          gender ?? "",
          kind ?? "",
          routeSlotID ?? -1,
          routeDrawableLocalIndex ?? -1,
          routeMeshPartIndex ?? -1,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["asset-library-clothes-items"],
      });
      queryClient.invalidateQueries({ queryKey: ["stats-overview"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (detailQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading asset detail...</div>;
  }

  if (detailQuery.isError || !detail) {
    return (
      <div className="rounded-2xl border border-dashed border-border/80 p-8 text-sm text-muted-foreground">
        Không tải được render asset detail.
      </div>
    );
  }

  const currentStorefrontMode = normalizeAssetStorefrontMode(
    selectedTextureVariant?.pricing?.priceType,
  );
  const currentStorefrontLabel = selectedTextureVariant?.pricing
    ? getAssetStorefrontLabel(currentStorefrontMode)
    : "Sẽ chọn khi lưu";
  const effectiveGlbPath =
    String(selectedTextureVariant?.glbPath ?? "").trim() || detail.glbPath;
  const effectiveGlbUrl =
    String(selectedTextureVariant?.glbUrl ?? "").trim() || detail.glbUrl;
  const compactGlbPath = compactAssetPath(effectiveGlbPath);

  return (
    <>
      <Suspense
        fallback={
          <div className="rounded-2xl border border-dashed border-border/80 p-8 text-sm text-muted-foreground">
            Đang tải preview module...
          </div>
        }
      >
        <RenderAssetViewer
          asset={detail}
          selectedTextureIndex={selectedTextureIndex}
          resolvedTexturePreview={texturePreviewQuery.data ?? null}
          onSelectedTextureIndexChange={(textureIndex) => {
            const next = new URLSearchParams(searchParams);
            next.set("textureIndex", String(textureIndex));
            setSearchParams(next, { replace: true });
          }}
          pricingPanel={
            canManagePricing ? (
              <Card className="border-border/80 bg-card/80">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Pricing editor</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid gap-3 md:grid-cols-5">
                    <div className="rounded-2xl border border-border/80 px-3 py-2 text-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Texture
                      </p>
                      <p className="mt-1 truncate font-medium">
                        #{effectiveTextureIndex} {selectedTextureVariant?.textureName ?? detail.assetName}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border/80 px-3 py-2 text-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Asset DB
                      </p>
                      <p className="mt-1 truncate font-medium">
                        {selectedTextureVariant?.pricing
                          ? `#${selectedTextureVariant.pricing.assetId}`
                          : "Sẽ tạo khi lưu"}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border/80 px-3 py-2 text-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Current price
                      </p>
                      <p className="mt-1 truncate">
                        {selectedTextureVariant?.pricing ? (
                          <span
                            className={`inline-flex rounded-md border px-2 py-1 font-semibold ${
                              selectedTextureVariant.pricing.price > 0
                                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                                : "border-amber-500/30 bg-amber-500/10 text-amber-700"
                            }`}
                          >
                            {formatRenderPrice(selectedTextureVariant.pricing.price)}
                          </span>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border/80 px-3 py-2 text-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Linked textures
                      </p>
                      <p className="mt-1 truncate font-medium">
                        {linkedTextureCount} / {detail.textureVariants.length}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-border/80 px-3 py-2 text-sm">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Storefront
                      </p>
                      <p className="mt-1 truncate">
                        <span
                          className={`inline-flex rounded-md border px-2 py-1 font-semibold ${
                            selectedTextureVariant?.pricing
                              ? getAssetStorefrontToneClass(currentStorefrontMode)
                              : "border-border bg-muted text-muted-foreground"
                          }`}
                        >
                          {currentStorefrontLabel}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-[minmax(0,180px)_minmax(0,190px)_minmax(0,160px)_minmax(0,1fr)_auto]">
                    <select
                      value={priceScope}
                      onChange={(event) =>
                        setPriceScope(
                          event.target.value === "all" ? "all" : "texture",
                        )
                      }
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="texture">Texture hiện tại</option>
                      <option value="all">Tất cả texture đã linked</option>
                    </select>
                    <select
                      value={draftPriceType}
                      onChange={(event) =>
                        setDraftPriceType(
                          normalizeAssetStorefrontMode(event.target.value),
                        )
                      }
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="money">Cửa hàng tiền ingame</option>
                      <option value="coin">Cửa hàng coin</option>
                      <option value="hidden">Ẩn khỏi cửa hàng</option>
                    </select>
                    <Input
                      value={draftPrice}
                      onChange={(event) => setDraftPrice(event.target.value)}
                      placeholder="0"
                    />
                    <Input
                      value={priceReason}
                      onChange={(event) => setPriceReason(event.target.value)}
                      placeholder="Reason"
                    />
                    <Button
                      onClick={() => {
                        const nextPrice = Number(draftPrice || "0");
                        if (!Number.isFinite(nextPrice)) {
                          toast.error("Giá không hợp lệ");
                          return;
                        }
                        updatePriceMutation.mutate({
                          assetId: resolvedAssetID,
                          textureIndex: effectiveTextureIndex,
                          price: nextPrice,
                          priceType: draftPriceType,
                          scope: priceScope,
                        });
                      }}
                      disabled={updatePriceMutation.isPending || !canSavePrice}
                    >
                      Save
                    </Button>
                  </div>

                  {priceScope === "texture" && !selectedTextureVariant?.pricing ? (
                    <div className="rounded-2xl border border-dashed border-border/80 px-3 py-2 text-sm text-muted-foreground">
                      Texture này chưa có record DB. Lưu lần đầu sẽ tự tạo record rồi áp storefront và giá.
                    </div>
                  ) : null}

                  {priceScope === "all" && missingTextureCount > 0 ? (
                    <div className="rounded-2xl border border-dashed border-border/80 px-3 py-2 text-sm text-muted-foreground">
                      {missingTextureCount} texture chưa có record DB sẽ được tự tạo khi lưu storefront và giá hàng loạt.
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ) : isAuthenticated ? (
              <div className="rounded-2xl border border-dashed border-border/80 p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Pricing is admin-only</p>
                <p className="mt-1">
                  Tài khoản <strong>{currentUser?.role ?? "user"}</strong> chỉ có quyền duyệt catalog. Chỉ admin mới được chỉnh storefront và giá.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border/80 p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Pricing is protected</p>
                <p className="mt-1">
                  Public catalog chỉ để duyệt. Đăng nhập bằng tài khoản admin để chỉnh storefront và giá.
                </p>
                <Button asChild variant="outline" className="mt-3">
                  <Link to="/login">
                    <LockKeyhole className="mr-2 h-4 w-4" />
                    Sign in
                  </Link>
                </Button>
              </div>
            )
          }
        />
      </Suspense>

      <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span>{formatRenderCollectionLabel(detail.collectionDisplay)}</span>
                <span>•</span>
                <span>{detail.pedModel}</span>
                <span>•</span>
                <span>{detail.pedGender}</span>
                <span>•</span>
                <span>{detail.kind}</span>
              </div>
              <p className="mt-2 text-lg font-semibold">{detail.assetName}</p>
              <p className="text-sm text-muted-foreground">
                {detail.slotLabel || detail.slotName} • slot {detail.slotId} • drawable {detail.drawableLocalIndex} • mesh {detail.meshPartIndex}
              </p>
            </div>
            <div className="rounded-full border border-border/80 px-3 py-1 text-xs text-muted-foreground">
              asset {detail.id}
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-2xl border border-border/80 bg-background/60 px-3 py-2 text-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Texture coverage
              </p>
              <p className="mt-1 font-medium">
                {linkedTextureCount} linked / {detail.textureVariants.length} total
              </p>
            </div>
            <div className="rounded-2xl border border-border/80 bg-background/60 px-3 py-2 text-sm">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Current texture
              </p>
              <p className="mt-1 font-medium">
                #{effectiveTextureIndex} {selectedTextureVariant?.textureName ?? detail.defaultTextureName}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-border/80 bg-background/60 px-3 py-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  Clothing shop hash
                </p>
                <p className="mt-1 truncate font-mono text-sm font-medium">
                  {selectedTextureHash}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Hash được build theo texture đang chọn ở preview.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => {
                  copyTextToClipboard(selectedTextureHash)
                    .then(() => {
                      toast.success("Đã copy clothing hash");
                    })
                    .catch((error: unknown) => {
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : "Không thể copy clothing hash",
                      );
                    });
                }}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy hash
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border/80 bg-muted/20 p-4">
        <a
          href={effectiveGlbUrl}
          target="_blank"
          rel="noreferrer"
          title={effectiveGlbPath}
          className="flex items-center justify-between gap-3 rounded-2xl border border-border/80 bg-background/60 px-3 py-3 transition-colors hover:bg-muted/40"
        >
          <div className="flex min-w-0 items-center gap-2">
            <Boxes className="h-4 w-4 flex-none" />
            <div className="min-w-0">
              <p className="truncate font-medium">{compactGlbPath.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {compactGlbPath.context}
              </p>
            </div>
          </div>
          <ExternalLink className="h-3.5 w-3.5 flex-none" />
        </a>
      </div>

      <div className="grid gap-3">
        <Card className="border-border/80 bg-card/80">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">DDS files</CardTitle>
              <div className="rounded-full border border-border/80 px-2 py-1 text-xs text-muted-foreground">
                {detail.ddsEntries.length.toLocaleString()}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.ddsEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Không có DDS riêng.</p>
            ) : (
              detail.ddsEntries.slice(0, 10).map((entry) => (
                <a
                  key={entry.url}
                  href={entry.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-2xl border border-border/80 px-3 py-2 text-xs hover:bg-muted/40"
                >
                  <span className="truncate">{entry.name}</span>
                  <ExternalLink className="h-3.5 w-3.5 flex-none" />
                </a>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-border/80 bg-card/80">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Texture variants</CardTitle>
              <div className="rounded-full border border-border/80 px-2 py-1 text-xs text-muted-foreground">
                {detail.textureVariants.length.toLocaleString()}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {detail.textureVariants.slice(0, 12).map((variant) =>
              variant.sourceUrl ? (
                <a
                  key={`${variant.textureIndex}-${variant.textureName}`}
                  href={variant.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-2xl border border-border/80 px-3 py-2 hover:bg-muted/40"
                >
                  <p className="truncate text-sm font-medium">
                    #{variant.textureIndex} {variant.textureName}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {variant.sourcePath}
                  </p>
                </a>
              ) : (
                <div
                  key={`${variant.textureIndex}-${variant.textureName}`}
                  className="block rounded-2xl border border-border/80 px-3 py-2"
                >
                  <p className="truncate text-sm font-medium">
                    #{variant.textureIndex} {variant.textureName}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {variant.sourcePath}
                  </p>
                </div>
              ),
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
